const DEFAULT_TRANSCRIPTION_RETRIES = 2;
const DEFAULT_TRANSCRIPTION_RETRY_DELAY_FACTOR_MS = 3000;
const DEFAULT_SLOW_TRANSCRIPTION_TIMEOUT_SECS = 10;
const NON_RETRYABLE_ERRORS = ["no_speech_detected", "file_too_large", "unsupported_format"];

import { useFetch } from "~vue/api";

const delay = async (delayMs) => {
    return new Promise((resolve) => setTimeout(resolve, delayMs));
};

/**
 * Encodes an audio blob to a base64 data URL string.
 * @param {Blob} blob
 * @returns {Promise<string>} base64-encoded data URL
 */
const encodeAudio = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

/**
 * Sends the audio as a base64-encoded JSON payload (legacy approach).
 * Used as a fallback when multipart upload fails due to network restrictions.
 * @param {Blob} blob
 * @returns {Promise<{data: import('vue').Ref, error: import('vue').Ref}>}
 */
const transcribeWithBase64 = async (blob) => {
    const dataUrl = await encodeAudio(blob);
    // Strip the "data:audio/...;base64," prefix — backend expects raw base64
    const base64data = dataUrl.split(",", 2)[1];
    return useFetch("/v1/transcription/json").post({ audio_stream: base64data }).json();
};

/**
 * Sends a request to the API to transcribe audio to text, with an automatic retry
 * mechanism and a callback for slow transcriptions.
 *
 * @param {Object} params
 * @param {Blob} params.blob Audio blob to transcribe.
 * @param {number=} params.maxRetries The number of times to retry transcription if the first
 * attempt fails.  Defaults to 2.
 * @param {number=} params.retryDelayFactorMs The number of additional milliseconds to delay before
 * each retry.  Each try will add this number cumulatively to the next delay.  Defaults to 3000 ms.
 * @param {number=} params.slowTimeoutSecs The number of seconds to elapse on each transcription try
 * before the `onSlow` callback is called.  Defaults to 10 seconds.
 * @param {function=} params.onStart Optional callback that will be called at the start of each
 * transcription try.
 * @param {function=} params.onSlow Optional callback that will be called on each transcription try
 * whenever `slowTimeoutSecs` has elapsed and transcription hasn't finished yet.
 * @param {function=} params.onError Optional callback that will be called after each try in the
 * event of a transcription error.
 *
 * @returns {Promise<string>} The transcribed text.
 */
export const transcribeRecording = async ({ blob, ...options }) => {
    const maxRetries = options.maxRetries ?? DEFAULT_TRANSCRIPTION_RETRIES;
    const retryDelayFactorMs = options.retryDelayFactorMs ?? DEFAULT_TRANSCRIPTION_RETRY_DELAY_FACTOR_MS;
    const slowTimeoutSecs = options.slowTimeoutSecs ?? DEFAULT_SLOW_TRANSCRIPTION_TIMEOUT_SECS;

    let retries = 0;
    let slowTimeout = null;
    let multipartBlocked = false;
    while (retries <= maxRetries) {
        // Each retry is delayed a couple of seconds depending on which retry we're at.  First one
        // is immediate, second is a few seconds later, etc.
        await delay(retries * retryDelayFactorMs);

        try {
            if (options.onStart) {
                options.onStart();
            }
            if (options.onSlow) {
                slowTimeout = setTimeout(options.onSlow, slowTimeoutSecs * 1000);
            }

            // Validate file size before upload (25MB limit)
            const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
            if (blob.size > MAX_FILE_SIZE) {
                const error = new Error("Recording too large (max 25MB). Try a shorter recording.");
                error.error_code = "file_too_large";
                throw error;
            }

            // Skip multipart if previously blocked by firewall, go straight to base64
            if (multipartBlocked) {
                const { data: b64Data, error: b64Error } = await transcribeWithBase64(blob);
                if (!b64Error.value && b64Data.value?.success) {
                    return b64Data.value.payload.content;
                }
                const fallbackErr = new Error(b64Data.value?.error_message || "Transcription failed");
                fallbackErr.error_code = b64Data.value?.error_code;
                throw fallbackErr;
            }

            const formData = new FormData();
            // Extract extension from blob MIME type for accurate file naming
            const mimeTypeExtensions = {
                "audio/mpeg": "mp3",
                "audio/mp4": "mp4",
                "audio/m4a": "m4a",
                "audio/x-m4a": "m4a",
                "audio/wav": "wav",
                "audio/webm": "webm",
            };
            // Handle more detailed offerings: audio/webm;codec=opus
            const extension = mimeTypeExtensions[blob.type.split(";")[0].trim()] || "webm";
            formData.append("audio_file", blob, `recording.${extension}`);

            const { data, error } = await useFetch("/v1/transcription").post(formData).json();

            if (error.value) {
                // If no error_code, the request likely never reached our backend
                // (e.g. blocked by client corporate proxy). Fall back to base64 JSON POST.
                if (!data.value?.error_code) {
                    multipartBlocked = true;
                    const { data: b64Data, error: b64Error } = await transcribeWithBase64(blob);
                    if (!b64Error.value && b64Data.value?.success) {
                        return b64Data.value.payload.content;
                    }
                    // Base64 also failed — build error from base64 response and throw
                    const fallbackErr = new Error(b64Data.value?.error_message || "Transcription failed");
                    fallbackErr.error_code = b64Data.value?.error_code;
                    throw fallbackErr;
                }
                const err = new Error(data.value?.error_message || "Transcription failed");
                err.error_code = data.value?.error_code;
                throw err;
            }

            return data.value.payload.content;
        } catch (err) {
            if (options.onError) {
                options.onError(err);
            }
            if (!NON_RETRYABLE_ERRORS.includes(err.error_code)) {
                if (retries < maxRetries) {
                    retries++;
                    continue;
                }
            }
            throw err;
        } finally {
            if (slowTimeout) {
                clearTimeout(slowTimeout);
                slowTimeout = null;
            }
        }
    }

    throw new Error("Transcription retries exhausted");
};
